import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { Tooltip, TooltipBody } from '@/components/ui/Tooltip'
import { useShop } from '@/contexts/ShopProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { POWERUPS, COSMETICS, PRODUCT_ICONS, vipTierName, type PowerupTier } from '@/game/catalog'
import { CHARACTERS, RARITY_COLORS } from '@/game/characters'
import { PROPS_CATALOG } from '@/game/props'
import { characterArt, powerupArt, productArt, STORE_BANNER } from '@/game/art'
import { ArtImage } from '@/components/ui/ArtImage'
import { useGameAudio } from '@/contexts/GameAudioProvider'

type Tab = 'powerups' | 'characters' | 'props' | 'skins' | 'trails' | 'premium'

const TABS: { id: Tab; label: string }[] = [
  { id: 'powerups', label: 'Powerups' },
  { id: 'characters', label: 'Heroes' },
  { id: 'props', label: 'Props' },
  { id: 'skins', label: 'Skins' },
  { id: 'trails', label: 'Trails' },
  { id: 'premium', label: 'Premium' },
]

const TIER_LABEL: Record<PowerupTier, string> = {
  basic: 'Basic',
  premium: 'Premium',
  legendary: 'Legendary',
}

const TIER_COLOR: Record<PowerupTier, string> = {
  basic: '#8f8aa8',
  premium: '#a678ff',
  legendary: '#ffc107',
}

const cardIn = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: Math.min(i * 0.045, 0.4), type: 'spring' as const, stiffness: 340, damping: 28 },
  }),
}

export function ShopScreen() {
  const [tab, setTab] = useState<Tab>('powerups')
  const { products, loadingProducts, productsError, iapConfigured, purchase, refreshProducts, playerLevel, buyPowerup, buyCharacter, equipCharacter, buyProp, equipProp } = useShop()
  const { save, update, spendCoins, spendGems } = useProgress()
  const audio = useGameAudio()
  const [pending, setPending] = useState<string | null>(null)

  const buyCosmetic = (id: string, kind: 'skin' | 'trail', cost: number, currency: 'coins' | 'gems') => {
    const ownedList = kind === 'skin' ? save.cosmetics.ownedSkins : save.cosmetics.ownedTrails
    if (ownedList.includes(id)) return
    const ok = currency === 'coins' ? spendCoins(cost) : spendGems(cost)
    if (!ok) return
    audio.play('confirm')
    update((s) => ({
      ...s,
      cosmetics:
        kind === 'skin'
          ? { ...s.cosmetics, ownedSkins: [...s.cosmetics.ownedSkins, id] }
          : { ...s.cosmetics, ownedTrails: [...s.cosmetics.ownedTrails, id] },
    }))
  }

  const buyProduct = async (id: string) => {
    setPending(id)
    try {
      const res = await purchase(id)
      audio.play(res.status === 'purchased' ? 'confirm' : 'error')
    } finally {
      setPending(null)
    }
  }

  return (
    <ScreenShell title="Shop">
      <div className="flex gap-2 mb-2 overflow-x-auto no-scrollbar pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              audio.play('hover')
              setTab(t.id)
            }}
            className={[
              'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition',
              tab === t.id ? 'bg-[var(--ember)] text-[#231404] shadow-raised' : 'bg-[var(--surface-raised)] text-[var(--text-dim)]',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {save.meta.vipTier > 0 && (
        <div className="mb-3 flex items-center justify-center gap-1.5 rounded-full bg-[var(--surface-sunken)] px-3 py-1 text-xs font-bold text-[var(--coin)]">
          <PixelIcon name="vip" size={13} />
          {vipTierName(save.meta.vipTier)} patron — thank you for keeping the hearth lit
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {tab === 'powerups' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {POWERUPS.map((p) => {
                const owned = save.powerups[p.id] ?? 0
                const locked = playerLevel < p.unlockLevel
                const maxed = owned >= p.maxQuantity
                return (
                  <motion.div
                    key={p.id}
                    custom={POWERUPS.indexOf(p)}
                    variants={cardIn}
                    initial="hidden"
                    animate="show"
                    whileHover={{ y: -2 }}
                    className="flex items-center gap-3 rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/30 p-3"
                  >
                    <Tooltip
                      content={
                        <TooltipBody
                          title={p.name}
                          lines={[p.description, locked ? `Unlocks after ${p.unlockLevel} levels` : null, `Carry up to ${p.maxQuantity}`]}
                        />
                      }
                    >
                      <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--surface-raised)] shadow-emboss" style={{ color: TIER_COLOR[p.tier] }}>
                        {powerupArt(p.id) ? (
                          <ArtImage src={powerupArt(p.id)!} alt={p.name} className="h-12 w-12" fallbackIcon={p.icon} fallbackColor={TIER_COLOR[p.tier]} />
                        ) : (
                          <PixelIcon name={p.icon} size={22} />
                        )}
                      </div>
                    </Tooltip>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold">{p.name}</span>
                        <span className="text-[10px] font-bold uppercase" style={{ color: TIER_COLOR[p.tier] }}>{TIER_LABEL[p.tier]}</span>
                        <span className="text-xs text-[var(--text-faint)]">x{owned}/{p.maxQuantity}</span>
                      </div>
                      <p className="text-sm text-[var(--text-dim)] leading-snug">
                        {locked ? `Unlocks after ${p.unlockLevel} levels` : p.description}
                      </p>
                    </div>
                    <GameButton
                      size="sm"
                      icon={locked ? 'lock' : p.currency === 'coins' ? 'coin' : 'gem'}
                      silent
                      disabled={locked || maxed || (p.currency === 'coins' ? save.economy.coins : save.economy.gems) < p.cost}
                      onClick={() => {
                        if (buyPowerup(p.id)) audio.play('powerup')
                        else audio.play('error')
                      }}
                    >
                      {maxed ? 'Max' : p.cost}
                    </GameButton>
                  </motion.div>
                )
              })}
            </div>
          )}

          {tab === 'characters' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CHARACTERS.map((c) => {
                const owned = save.cosmetics.ownedCharacters.includes(c.id)
                const equipped = save.cosmetics.equippedCharacter === c.id
                const claimable = !owned && !c.iapOnly && c.unlockLevel != null && playerLevel >= c.unlockLevel
                const buyable = !owned && !c.iapOnly && !!c.price && !claimable
                return (
                  <motion.div
                    key={c.id}
                    custom={CHARACTERS.indexOf(c)}
                    variants={cardIn}
                    initial="hidden"
                    animate="show"
                    whileHover={{ y: -2 }}
                    className="flex items-center gap-3 rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss p-3"
                    style={{ border: `1px solid ${RARITY_COLORS[c.rarity]}55` }}
                  >
                    <Tooltip content={<TooltipBody title={c.name} lines={[c.description, `${c.rarity} hero`]} />}>
                      <span
                        className="relative h-16 w-16 shrink-0 overflow-hidden rounded-[var(--r-sm)]"
                        style={{
                          background: `radial-gradient(circle at 50% 40%, ${c.palette.base}22, var(--surface-sunken))`,
                          boxShadow: `inset 0 0 0 2px ${RARITY_COLORS[c.rarity]}66`,
                        }}
                      >
                        {characterArt(c.id) ? (
                          <ArtImage src={characterArt(c.id)!} alt={c.name} className="h-full w-full" fallbackColor={c.palette.base} />
                        ) : (
                          <span
                            className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full"
                            style={{ background: `radial-gradient(circle at 35% 30%, ${c.palette.emissive}, ${c.palette.base} 65%)` }}
                          />
                        )}
                      </span>
                    </Tooltip>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold">{c.name}</span>
                        <span className="text-[10px] font-bold uppercase" style={{ color: RARITY_COLORS[c.rarity] }}>{c.rarity}</span>
                      </div>
                      <p className="text-sm text-[var(--text-dim)] leading-snug">{c.description}</p>
                    </div>
                    {equipped ? (
                      <GameButton size="sm" variant="ghost" silent disabled>
                        Equipped
                      </GameButton>
                    ) : owned ? (
                      <GameButton size="sm" variant="secondary" silent onClick={() => { if (equipCharacter(c.id)) audio.play('confirm') }}>
                        Equip
                      </GameButton>
                    ) : c.iapOnly ? (
                      <GameButton size="sm" icon="vip" silent onClick={() => setTab('premium')}>
                        Premium
                      </GameButton>
                    ) : claimable ? (
                      <GameButton size="sm" icon="star" silent onClick={() => { if (buyCharacter(c.id)) audio.play('levelup'); else audio.play('error') }}>
                        Claim
                      </GameButton>
                    ) : buyable ? (
                      <GameButton
                        size="sm"
                        icon={c.price?.gems ? 'gem' : 'coin'}
                        silent
                        disabled={(c.price?.gems ?? 0) > save.economy.gems || (c.price?.coins ?? 0) > save.economy.coins}
                        onClick={() => { if (buyCharacter(c.id)) audio.play('levelup'); else audio.play('error') }}
                      >
                        {c.price?.gems ?? c.price?.coins}
                      </GameButton>
                    ) : (
                      <GameButton size="sm" icon="lock" silent disabled>
                        Lv {c.unlockLevel}
                      </GameButton>
                    )}
                  </motion.div>
                )
              })}
            </div>
          )}

          {tab === 'props' && (
            <div className="grid grid-cols-2 gap-2">
              {PROPS_CATALOG.map((p) => {
                const owned = save.cosmetics.ownedProps.includes(p.id)
                const equipped = save.cosmetics.equippedProps.includes(p.id)
                const cost = p.price.gems ?? p.price.coins ?? 0
                const currency: 'coins' | 'gems' = p.price.gems ? 'gems' : 'coins'
                const affordable = currency === 'gems' ? save.economy.gems >= cost : save.economy.coins >= cost
                return (
                  <motion.div
                    key={p.id}
                    custom={PROPS_CATALOG.indexOf(p)}
                    variants={cardIn}
                    initial="hidden"
                    animate="show"
                    whileHover={{ y: -2 }}
                    className="flex flex-col items-center gap-2 rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss p-3 text-center"
                    style={{ border: `1px solid ${RARITY_COLORS[p.rarity]}55` }}
                  >
                    <Tooltip content={<TooltipBody title={p.name} lines={p.effects.map((e) => e.description)} />}>
                      <span
                        className="grid h-11 w-11 place-items-center rounded-[var(--r-sm)] shadow-emboss"
                        style={{ background: 'var(--surface-raised)', color: p.palette.glow }}
                      >
                        <PixelIcon name={p.icon} size={22} />
                      </span>
                    </Tooltip>
                    <div>
                      <div className="text-sm font-display font-bold leading-tight">{p.name}</div>
                      <div className="text-[10px] font-bold uppercase" style={{ color: RARITY_COLORS[p.rarity] }}>
                        {p.rarity} · {p.slot}
                      </div>
                      <p className="mt-0.5 text-xs text-[var(--text-faint)] line-clamp-2">{p.effects[0].description}</p>
                    </div>
                    {equipped ? (
                      <GameButton size="sm" variant="ghost" fullWidth silent disabled>
                        Equipped
                      </GameButton>
                    ) : owned ? (
                      <GameButton size="sm" variant="secondary" fullWidth silent onClick={() => { if (equipProp(p.id)) audio.play('confirm') }}>
                        Equip
                      </GameButton>
                    ) : (
                      <GameButton
                        size="sm"
                        icon={currency === 'gems' ? 'gem' : 'coin'}
                        fullWidth
                        silent
                        disabled={!affordable}
                        onClick={() => { if (buyProp(p.id)) audio.play('levelup'); else audio.play('error') }}
                      >
                        {cost}
                      </GameButton>
                    )}
                  </motion.div>
                )
              })}
            </div>
          )}

          {(tab === 'skins' || tab === 'trails') && (
            <div className="grid grid-cols-2 gap-2">
              {COSMETICS.filter((c) => c.kind === (tab === 'skins' ? 'skin' : 'trail')).map((c, i) => {
                const owned = (tab === 'skins' ? save.cosmetics.ownedSkins : save.cosmetics.ownedTrails).includes(c.id)
                const equipped = (tab === 'skins' ? save.cosmetics.equippedSkin : save.cosmetics.equippedTrail) === c.id
                return (
                  <motion.div
                    key={c.id}
                    custom={i}
                    variants={cardIn}
                    initial="hidden"
                    animate="show"
                    whileHover={{ y: -2 }}
                    className="rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/30 p-3 flex flex-col items-center gap-2"
                  >
                    <div className="w-14 h-14 rounded-full" style={{ background: `radial-gradient(circle at 35% 30%, #fff6, ${c.color})`, boxShadow: `0 0 18px ${c.color}66` }} />
                    <div className="text-center">
                      <div className="font-display font-bold text-sm">{c.name}</div>
                      <p className="text-xs text-[var(--text-faint)]">{c.description}</p>
                    </div>
                    {equipped ? (
                      <GameButton size="sm" variant="ghost" fullWidth silent disabled>
                        Equipped
                      </GameButton>
                    ) : owned ? (
                      <GameButton
                        size="sm"
                        variant="secondary"
                        fullWidth
                        silent
                        onClick={() =>
                          update((s) => ({
                            ...s,
                            cosmetics:
                              c.kind === 'skin'
                                ? { ...s.cosmetics, equippedSkin: c.id }
                                : { ...s.cosmetics, equippedTrail: c.id },
                          }))
                        }
                      >
                        Equip
                      </GameButton>
                    ) : (
                      <GameButton
                        size="sm"
                        icon={c.currency === 'coins' ? 'coin' : 'gem'}
                        fullWidth
                        silent
                        disabled={(c.currency === 'coins' ? save.economy.coins : save.economy.gems) < c.cost}
                        onClick={() => buyCosmetic(c.id, c.kind as 'skin' | 'trail', c.cost, c.currency)}
                      >
                        {c.cost}
                      </GameButton>
                    )}
                  </motion.div>
                )
              })}
            </div>
          )}

          {tab === 'premium' && (
            <ProductList
              products={products}
              loading={loadingProducts}
              error={productsError}
              configured={iapConfigured}
              pending={pending}
              onBuy={buyProduct}
              onRetry={refreshProducts}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </ScreenShell>
  )
}

function ProductList({
  products,
  loading,
  error,
  configured,
  pending,
  onBuy,
  onRetry,
}: {
  products: { id: string; title: string; description: string; priceLabel: string }[]
  loading: boolean
  error: string | null
  configured: boolean
  pending: string | null
  onBuy: (id: string) => void
  onRetry: () => void
}) {
  if (loading) {
    return (
      <div className="grid place-items-center py-12 text-[var(--text-faint)]">
        <PixelIcon name="loading" size={28} className="animate-spin" />
        <span className="mt-2 text-sm">Loading store…</span>
      </div>
    )
  }
  if (error) {
    return (
      <div className="grid place-items-center py-12 gap-3 text-center">
        <PixelIcon name="error" size={28} />
        <p className="text-[var(--text-dim)]">{error}</p>
        <GameButton size="sm" variant="secondary" icon="retry" onClick={onRetry}>
          Retry
        </GameButton>
      </div>
    )
  }
  if (products.length === 0) {
    return <div className="grid place-items-center py-12 text-[var(--text-faint)]">Nothing here yet.</div>
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div className="sm:col-span-2 relative overflow-hidden rounded-[var(--r-lg)] shadow-raised border border-[var(--edge)]/40" style={{ aspectRatio: '3 / 1' }}>
        <img src={STORE_BANNER} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-3 left-4 right-4">
          <div className="font-display text-lg font-black text-[var(--text)]">Keep the hearth lit</div>
          <p className="text-xs text-[var(--text-dim)]">Support Ember's journey home and unlock the whole cast.</p>
        </div>
      </div>
      {!configured && (
        <div className="sm:col-span-2 rounded-[var(--r-md)] bg-[var(--surface-sunken)] px-4 py-2 text-sm text-[var(--text-faint)] text-center">
          Purchases are not available in this build.
        </div>
      )}
      {products.map((p, i) => (
        <motion.div
          key={p.id}
          custom={i}
          variants={cardIn}
          initial="hidden"
          animate="show"
          whileHover={{ y: -2 }}
          className="flex items-center gap-3 rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/30 p-3"
        >
          <Tooltip content={<TooltipBody title={p.title} lines={[p.description, p.priceLabel]} />}>
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--surface-raised)] text-[var(--coin)] shadow-emboss">
              {productArt(p.id) ? (
                <ArtImage src={productArt(p.id)!} alt={p.title} className="h-12 w-12" fallbackIcon={PRODUCT_ICONS[p.id] ?? 'gift'} fallbackColor="var(--coin)" />
              ) : (
                <PixelIcon name={PRODUCT_ICONS[p.id] ?? 'gift'} size={22} />
              )}
            </div>
          </Tooltip>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold">{p.title}</div>
            <p className="text-sm text-[var(--text-dim)] leading-snug">{p.description}</p>
          </div>
          <GameButton
            size="sm"
            loading={pending === p.id}
            disabled={!configured}
            onClick={() => onBuy(p.id)}
          >
            {p.priceLabel}
          </GameButton>
        </motion.div>
      ))}
    </div>
  )
}
